import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiRequest } from '../api';
import { queryKeys } from '../queryKeys';
import type { AssignmentStatus, QaAssignment, SyncResult, WeeklyChartPoint } from '../types';

type AssignmentFilters = {
  reviewer?: string;
  status?: AssignmentStatus | 'all';
};

export function useQaAssignments(filters: AssignmentFilters = {}) {
  const params: Record<string, string | undefined> = {};
  if (filters.reviewer) params.reviewer = filters.reviewer;
  if (filters.status && filters.status !== 'all') params.status = filters.status;
  return useQuery({
    queryKey: queryKeys.qaAssignments(params),
    queryFn: () => apiRequest<QaAssignment[]>('/api/qa-assignments', { params }),
  });
}

export function useQaAssignmentsChart() {
  return useQuery({
    queryKey: queryKeys.qaAssignmentsChart,
    queryFn: () => apiRequest<WeeklyChartPoint[]>('/api/qa-assignments/chart'),
  });
}

export function useUpdateQaAssignment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: Partial<QaAssignment> }) =>
      apiRequest<QaAssignment>(`/api/qa-assignments/${id}`, { method: 'PATCH', body: patch }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qa-assignments'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.stats });
    },
  });
}

export function useDeleteQaAssignment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => apiRequest<void>(`/api/qa-assignments/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qa-assignments'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.stats });
    },
  });
}

export function useBulkDeleteQaAssignments() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: number[]) =>
      Promise.all(ids.map((id) => apiRequest<void>(`/api/qa-assignments/${id}`, { method: 'DELETE' }))),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qa-assignments'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.stats });
    },
  });
}

export function useSyncQaAssignments() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (days: number) =>
      apiRequest<SyncResult>('/api/qa-assignments/sync', { method: 'POST', body: { days } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qa-assignments'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.stats });
    },
  });
}
